import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget — free, privacy-preserving CAPTCHA.
 *
 * Renders the challenge box and calls `onVerify(token)` when solved. The
 * token is posted to the backend (org signup), which verifies it server-side
 * (apps/accounts/services/turnstile.py). The site key is public; the secret
 * lives only in the backend env.
 *
 * When `VITE_TURNSTILE_SITE_KEY` is unset we fall back to the production
 * site key. Cloudflare's always-passing TEST site key is
 * `1x00000000000000000000AA` for local runs without a real key.
 */

const SITE_KEY = (
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ??
  "0x4AAAAAADv2miIP-htCAC6w"
).trim();

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      theme?: "auto" | "light" | "dark";
      language?: string;
    },
  ) => string;
  remove: (id: string) => void;
  reset: (id?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

/** Load the Turnstile script once, shared across every widget instance. */
function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile")));
      if (window.turnstile) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile script failed to load"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

type Props = {
  /** Called with the solution token when the challenge is solved, and with
   * an empty string when it expires or errors (so the caller can clear it). */
  onVerify: (token: string) => void;
  className?: string;
  /** Locale for the widget UI (ISO 639-1, e.g. "fr"). Omit for "auto"
   * (browser language). Pass the app's current locale so the captcha
   * matches the selected site language. */
  language?: string;
};

export function TurnstileWidget({ onVerify, className, language }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Keep the latest callback without re-rendering the widget.
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        // Guard against React 18 StrictMode double-invoke.
        if (widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => onVerifyRef.current(token),
          "expired-callback": () => onVerifyRef.current(""),
          "error-callback": () => onVerifyRef.current(""),
          ...(language ? { language } : {}),
        });
      })
      .catch(() => {
        /* script failed to load — backend still enforces if configured */
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* already gone */
        }
        widgetIdRef.current = null;
      }
    };
    // Re-render when the locale changes so the widget UI follows the site
    // language (cleanup removes the old widget first).
  }, [language]);

  return <div ref={containerRef} className={className} />;
}
